import {
  MoreVert,
  TuneOutlined,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import {
  Button,
  Menu,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider,
  Box,
  Typography,
} from "@mui/material";
import { useState, MouseEvent } from "react";
import { makeSxStyles } from "ui";

type RoutePlannerMapFilterProps = {
  onShowSkippedChange: (show: boolean) => void;
  onShowRegularChange: (show: boolean) => void;
  onShowDelayedChange: (show: boolean) => void;
  onShowFlatRateChange: (show: boolean) => void;
  onShowOnDemandChange: (show: boolean) => void;
  onShowUnassignedActiveChange: (show: boolean) => void;
  onShowUnassignedInactiveChange: (show: boolean) => void;
  onBulkEditStartTime: () => void;
  showSkipped: boolean | undefined;
  showRegular: boolean | undefined;
  showDelayed: boolean | undefined;
  showFlatRate: boolean | undefined;
  showOnDemand: boolean | undefined;
  showUnassignedActive: boolean | undefined;
  showUnassignedInactive: boolean | undefined;
};

export const RoutePlannerMapFilters: React.FC<RoutePlannerMapFilterProps> = ({
  onShowSkippedChange,
  onShowRegularChange,
  onShowDelayedChange,
  onShowFlatRateChange,
  onShowOnDemandChange,
  onShowUnassignedActiveChange,
  onShowUnassignedInactiveChange,
  onBulkEditStartTime,
  showSkipped,
  showRegular,
  showDelayed,
  showFlatRate,
  showOnDemand,
  showUnassignedActive,
  showUnassignedInactive,
}) => {
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [customerTypeExpanded, setCustomerTypeExpanded] = useState(false);
  const isAnchorElOpen = Boolean(anchorEl);
  const isMenuAnchorElOpen = Boolean(menuAnchorEl);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <>
      <div>
        <Button
          sx={styles.menuBoxItem}
          aria-label="more"
          id="filter-button"
          className={isAnchorElOpen ? "menuBoxItemActive" : ""}
          onClick={handleClick}
        >
          <TuneOutlined sx={{ color: "#C1C7CE" }} />
        </Button>
        <Menu
          id="filter-menu"
          MenuListProps={{
            "aria-labelledby": "filter-button",
          }}
          anchorEl={anchorEl}
          open={isAnchorElOpen}
          onClose={handleClose}
          className={isAnchorElOpen ? "onActiveFiltersOpen" : ""}
          sx={styles.filterMenu}
        >
          <MenuItem key="1" sx={styles.menuItem}>
            <FormControlLabel
              value="showRegular"
              control={
                <Switch
                  checked={showRegular}
                  color="primary"
                  onChange={(e) => onShowRegularChange(e.target.checked)}
                />
              }
              label="Regular Orders"
              labelPlacement="start"
            />
          </MenuItem>
          <MenuItem key="2" sx={styles.menuItem}>
            <FormControlLabel
              value="showDelayed"
              control={
                <Switch
                  checked={showDelayed}
                  color="primary"
                  onChange={(e) => onShowDelayedChange(e.target.checked)}
                />
              }
              label="Delayed Orders"
              labelPlacement="start"
            />
          </MenuItem>
          <MenuItem key="3" sx={styles.menuItem}>
            <FormControlLabel
              value="showSkipped"
              control={
                <Switch
                  checked={showSkipped}
                  color="primary"
                  onChange={(e) => onShowSkippedChange(e.target.checked)}
                />
              }
              label="Skipped Orders"
              labelPlacement="start"
            />
          </MenuItem>

          <Divider sx={{ mt: "9px", mb: "9px" }} />

          <MenuItem
            key="4"
            sx={styles.customerTypeHeader}
            onClick={() => setCustomerTypeExpanded(!customerTypeExpanded)}
          >
            <Box sx={styles.customerTypeLabel}>
              <Typography variant="body1">Customer Type</Typography>
              {customerTypeExpanded ? <ArrowDropUp /> : <ArrowDropDown />}
            </Box>
          </MenuItem>

          {customerTypeExpanded && (
            <>
              <MenuItem key="5" sx={styles.subMenuItem}>
                <FormControlLabel
                  value="showFlatRate"
                  control={
                    <Switch
                      checked={showFlatRate}
                      color="primary"
                      onChange={(e) => onShowFlatRateChange(e.target.checked)}
                    />
                  }
                  label="Flat Rate"
                  labelPlacement="start"
                />
              </MenuItem>
              <MenuItem key="6" sx={styles.subMenuItem}>
                <FormControlLabel
                  value="showOnDemand"
                  control={
                    <Switch
                      checked={showOnDemand}
                      color="primary"
                      onChange={(e) => onShowOnDemandChange(e.target.checked)}
                    />
                  }
                  label="On Demand"
                  labelPlacement="start"
                />
              </MenuItem>
            </>
          )}

          <Divider sx={{ mt: "9px", mb: "9px" }} />

          <MenuItem key="7" sx={styles.menuItem}>
            <FormControlLabel
              value="showUnassignedActive"
              control={
                <Switch
                  checked={showUnassignedActive}
                  color="primary"
                  onChange={(e) =>
                    onShowUnassignedActiveChange(e.target.checked)
                  }
                />
              }
              label="Unassigned Active"
              labelPlacement="start"
            />
          </MenuItem>
          <MenuItem key="8" sx={styles.menuItem}>
            <FormControlLabel
              value="showUnassignedInactive"
              control={
                <Switch
                  checked={showUnassignedInactive}
                  color="primary"
                  onChange={(e) =>
                    onShowUnassignedInactiveChange(e.target.checked)
                  }
                />
              }
              label="Unassigned Inactive"
              labelPlacement="start"
            />
          </MenuItem>
        </Menu>
      </div>
      <div>
        <Button
          sx={styles.menuBoxItem}
          aria-label="more"
          id="filter-button"
          className={isMenuAnchorElOpen ? "menuBoxItemActive" : ""}
          onClick={handleMenuClick}
        >
          <MoreVert sx={{ color: "#C1C7CE" }} />
        </Button>
        <Menu
          id="actions-menu"
          MenuListProps={{
            "aria-labelledby": "filter-button",
          }}
          anchorEl={menuAnchorEl}
          open={isMenuAnchorElOpen}
          onClose={handleMenuClose}
          className={isMenuAnchorElOpen ? "onActiveFiltersOpen" : ""}
          sx={styles.actionsMenu}
        >
          <MenuItem
            key="1"
            sx={{
              ...styles.menuItem,
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}
            onClick={() => {
              onBulkEditStartTime();
              handleMenuClose();
            }}
          >
            Change Start Time For All Routes
          </MenuItem>
        </Menu>
      </div>
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  menuBoxItem: {
    width: "36px",
    height: "36px",
    minWidth: "unset",
    padding: "unset",
    borderRadius: "100%",

    "&.menuBoxItemActive": {
      textDecoration: "none",
      backgroundColor: "rgba(144, 202, 249, 0.08) !important",
    },
  },
  menuItem: {
    paddingTop: "1px",
    paddingBottom: "1px",
  },
  customerTypeHeader: {
    paddingTop: "1px",
    paddingBottom: "1px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.04)",
    },
  },
  customerTypeLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  subMenuItem: {
    paddingTop: "1px",
    paddingBottom: "1px",
    paddingLeft: "24px", // Indent for sub-items
  },
  filterMenu: {
    "& .MuiMenu-list": {
      backgroundColor: "#1A1C1E",
      minWidth: "300px",
    },
    "& .MuiMenu-list > li label": {
      marginLeft: "0",
      width: "100%",
      justifyContent: "space-between",
    },
  },
  actionsMenu: {
    "& .MuiMenu-list": {
      backgroundColor: "#1A1C1E",
      maxWidth: "243px",
    },
    "& .MuiMenu-list > li label": {
      marginLeft: "0",
      width: "100%",
      justifyContent: "space-between",
    },
  },
}));
